// src/modules/compass/domainState.js
//
// Compass-owned reasoning judgment.
//
// Deliberately NOT in src/config/. What the domains ARE is shared application
// vocabulary (src/config/domains.js). How much understanding counts as
// sufficient is a Compass judgment about its own line of inquiry. Overview,
// Pulse, and Reports must not silently inherit Compass's opinion of
// sufficiency — if they need a notion of coverage, they state their own.

import { DOMAINS, UNASSIGNED, resolveDomain } from '../../config/domains';

// Named so the judgment is visible and adjustable rather than a magic number
// buried in a comparison. EMERGING_MIN is the first count that is no longer
// empty; ESTABLISHED_MIN is the first count Compass treats as established.
export const STATE_THRESHOLDS = {
  EMERGING_MIN: 1,
  ESTABLISHED_MIN: 3,
};

// Only confirmed understanding counts toward a domain's state. 'proposed' is
// not yet believed and 'retracted' is no longer believed; neither is evidence
// of coverage.
export function deriveDomainState(units) {
  const list = Array.isArray(units) ? units : [];
  const confirmed = list.filter(u => u && u.status === 'confirmed').length;

  if (confirmed >= STATE_THRESHOLDS.ESTABLISHED_MIN) return 'established';
  if (confirmed >= STATE_THRESHOLDS.EMERGING_MIN) return 'emerging';
  return 'empty';
}

// Groups units into the six domains in DOMAINS order, with UNASSIGNED last.
//
// INVARIANT: no unit is ever dropped. Every input unit lands in exactly one
// bucket. resolveDomain is total, so a null, empty, or unrecognised
// domain_key resolves to UNASSIGNED rather than falling through to nothing.
// The sum of the returned `units` arrays always equals the input length.
//
// The UNASSIGNED bucket is always present, even when empty — the caller
// decides whether an empty Unsorted bucket is worth rendering.
export function groupUnitsByDomain(units) {
  const list = Array.isArray(units) ? units : [];

  const buckets = new Map();
  for (const domain of DOMAINS) {
    buckets.set(domain.key, []);
  }
  buckets.set(UNASSIGNED.key, []);

  for (const unit of list) {
    if (!unit) continue;
    const domain = resolveDomain(unit.domain_key);
    buckets.get(domain.key).push(unit);
  }

  const ordered = [...DOMAINS, UNASSIGNED];
  return ordered.map(domain => {
    const bucketUnits = buckets.get(domain.key);
    return {
      domain,
      units: bucketUnits,
      state: deriveDomainState(bucketUnits),
    };
  });
}
